import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Bot, LogOut, Shield, Crown, LayoutDashboard, Map, MessagesSquare, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const OAUTH_ERR_SL: Record<string, string> = {
  token:
    "Discord je zavrnil menjavo kode (token). Najpogosteje: v Portal → OAuth2 → Redirects manjka točen callback URL za ta host/port. Druga pogosta napaka: CLIENT_ID / CLIENT_SECRET / bot token niso iz iste aplikacije (invalid_client, „Unknown Application“). Odpri /api/oauth-config (zadnje 4 števke client_id in ujemanje z bot tokenom).",
  state: "OAuth state je neveljaven ali potekel. Znova klikni Log in with Discord.",
  code: "Discord ni vrnil kode. Znova poskusi prijavo.",
  session: "Seje ni bilo mogoce shraniti. Preveri SECRET_KEY v .env in poskusi znova.",
  user: "Uporabnika ni bilo mogoce prebrati po prijavi.",
};

export function Layout({ children }: { children: ReactNode }) {
  const { user, isAuthLoading, isLoggedIn, isDashboardOwner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [oauthBannerDismissed, setOauthBannerDismissed] = useState(false);
  const oauthErr = useMemo(() => {
    const q = new URLSearchParams(location.search).get("err");
    return q && OAUTH_ERR_SL[q] ? q : null;
  }, [location.search]);

  useEffect(() => {
    if (oauthErr) setOauthBannerDismissed(false);
  }, [oauthErr]);
  const { data: pubStats } = useQuery({
    queryKey: ["publicStats"],
    queryFn: () => api.publicStats(),
    staleTime: 120_000,
  });
  const communityDiscordUrl = pubStats?.community_discord_url ?? null;

  return (
    <div className="min-h-screen min-h-dvh flex flex-col max-w-[100vw]">
      <header className="sticky top-0 z-40 border-b border-border/60 glass pt-[env(safe-area-inset-top,0px)]">
        <div className="container flex min-h-14 sm:min-h-16 items-center justify-between gap-2 py-1.5 sm:py-0 min-w-0">
          <Link
            to="/"
            className="flex items-center gap-1.5 sm:gap-2 group min-w-0 shrink"
          >
            <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center transition-transform group-hover:scale-110">
              <img src="/areon-logo.png" alt="Areon" className="h-full w-full object-contain" />
            </div>
            <span className="text-base sm:text-xl font-black tracking-tight uppercase truncate text-gradient">
              Areon
            </span>
            {pubStats && (
              <Badge variant="outline" className="ml-2 bg-primary/10 border-primary/20 text-[10px] font-mono font-medium text-primary">
                {pubStats.guild_count} SERVERS
              </Badge>
            )}
          </Link>

          <nav className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/roadmap")}
              className="h-9 px-2 sm:px-3 hidden min-[480px]:inline-flex"
              aria-label="Roadmap"
            >
              <Map className="h-4 w-4" />
              <span className="hidden sm:inline">Roadmap</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/radio")}
              className={`h-9 px-2 sm:px-3 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 ${
                location.pathname === "/radio" ? "bg-emerald-500/10" : ""
              }`}
              aria-label="Radio"
            >
              <Radio className="h-4 w-4 mr-0.5 sm:mr-1.5 shrink-0" />
              <span className="hidden sm:inline">Radio</span>
            </Button>
            {communityDiscordUrl && (
              <a href={communityDiscordUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 px-2 sm:px-3 text-primary hover:text-primary"
                  aria-label="Community Discord"
                >
                  <MessagesSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Discord</span>
                </Button>
              </a>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/premium")}
              className="h-9 px-2 sm:px-3 text-amber-400 hover:text-amber-300"
              aria-label="Premium"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Premium</span>
            </Button>
            {isAuthLoading ? (
              <div
                className="h-9 w-[10rem] rounded-md bg-muted/50 animate-pulse"
                aria-hidden
              />
            ) : isLoggedIn && user ? (
              <>
                {isDashboardOwner && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate("/admin")}
                    className="h-9 px-2 sm:px-3"
                    aria-label="Admin panel"
                  >
                    <Shield className="h-4 w-4" />
                    <span className="hidden sm:inline">Admin</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="h-9 px-2 sm:px-3"
                  aria-label="Dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
                <div className="flex items-center gap-1 sm:gap-2 pl-1 sm:pl-2 border-l border-border">
                  {user.avatar && (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`}
                      alt=""
                      className="h-7 w-7 rounded-full shrink-0 hidden min-[400px]:block"
                    />
                  )}
                  <span className="text-sm font-medium hidden md:inline max-w-[8rem] truncate">
                    {user.username}
                  </span>
                  {isDashboardOwner && (
                    <Badge variant="premium" className="hidden lg:inline-flex">
                      DEV
                    </Badge>
                  )}
                  <a href="/logout">
                    <Button size="icon" variant="ghost" title="Log out" className="h-9 w-9 shrink-0">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </>
            ) : (
              <a href="/login">
                <Button size="sm" className="h-9 px-2.5 sm:px-3 text-xs sm:text-sm whitespace-normal text-center leading-tight max-w-[9.5rem] sm:max-w-none">
                  <span className="sm:hidden">Log in (Discord)</span>
                  <span className="hidden sm:inline">Log in with Discord</span>
                </Button>
              </a>
            )}
          </nav>
        </div>
      </header>

      {oauthErr && !oauthBannerDismissed && (
        <div className="border-b border-destructive/40 bg-destructive/15">
          <div className="container py-3 text-sm flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-destructive-foreground/95 pr-2">
              <span className="font-semibold">Prijava z Discord: </span>
              {OAUTH_ERR_SL[oauthErr]}
            </p>
            <div className="flex flex-wrap gap-2 shrink-0">
              <a
                href="/api/oauth-config"
                className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                Seznam Redirect URI (JSON)
              </a>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
                onClick={() => {
                  setOauthBannerDismissed(true);
                  navigate({ pathname: location.pathname, search: "" }, { replace: true });
                }}
              >
                Zapri
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="container flex-1 min-w-0 py-6 sm:py-8">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="h-full"
        >
          {children}
        </motion.div>
      </main>

      <footer className="border-t border-border/60 mt-16 py-8">
        <div className="container flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-sm text-muted-foreground flex-wrap">
          <Link to="/roadmap" className="hover:text-foreground transition-colors">
            Roadmap
          </Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </Link>
          {communityDiscordUrl && (
            <>
              <span className="hidden sm:inline">·</span>
              <a
                href={communityDiscordUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                <MessagesSquare className="h-3.5 w-3.5 opacity-80" />
                Community Discord
              </a>
            </>
          )}
          <span className="hidden sm:inline">·</span>
          <span>© {new Date().getFullYear()} Areon · Built with ❤</span>
        </div>
      </footer>
    </div>
  );
}
